import { useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router';
import { api, errorMessage } from '../../shared/api/client';
import type { VisitorDetail, VisitorStage } from '../../shared/api/types';
import { Alert, Button, Card, EmptyState, ErrorState, Loading } from '../../shared/ui/components';
import { useAsync } from '../../shared/ui/useAsync';
import { useMe } from '../session/SessionProvider';
import { STAGES, stageLabel, whatsappNumber } from './shared';
import { VisitorForm, visitorAsPerson } from './VisitorForm';
import { personOptions, SearchSelect } from '../../shared/ui/SearchSelect';

const KIND: Record<string, string> = { nota: 'Nota', llamada: 'Llamada', visita: 'Visita', mensaje: 'Mensaje', etapa: 'Etapa' };

/** Ficha de seguimiento de un visitante. */
export function VisitorPage() {
  const { visitorId = '' } = useParams();
  const me = useMe();
  const navigate = useNavigate();
  const churchId = me.activeChurchId;
  const base = `/multichurch/churches/${churchId}/visitors/${visitorId}`;
  const data = useAsync((signal) => (churchId ? api.get<VisitorDetail>(base, signal) : Promise.resolve(null)), [base]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [editing, setEditing] = useState(false);
  const [kind, setKind] = useState<'llamada' | 'mensaje' | 'visita' | 'nota'>('llamada');
  const [body, setBody] = useState('');

  async function run(action: () => Promise<unknown>) {
    setBusy(true);
    setError(null);
    try {
      await action();
      data.reload();
      return true;
    } catch (e) {
      setError(errorMessage(e));
      return false;
    } finally {
      setBusy(false);
    }
  }

  if (!churchId) return <EmptyState title="Elige primero una iglesia en el selector de arriba." />;
  if (data.status === 'loading') return <div className="page"><Loading /></div>;
  if (data.status === 'error') return <div className="page"><ErrorState error={data.error} onRetry={data.reload} /></div>;
  const v = data.data;
  if (!v) return null;

  return (
    <div className="page page--narrow">
      <p className="small">
        <Link to="/app/miembros?tab=visitas">← Visitas</Link>
      </p>
      <header className="page__header">
        <h1>
          {v.givenName} {v.familyName}
        </h1>
        <p className="muted">
          {stageLabel(v.stage)} · primera visita {v.firstVisit.split('-').reverse().join('/')}
          {v.source === 'formulario' ? ' · se registró con el enlace' : ''}
        </p>
        {me.can('members.manage') && v.stage !== 'miembro' ? (
          <Button
            variant="secondary"
            onClick={() => navigate('/app/miembros', { state: { fromVisit: { visitorId: v.id, person: visitorAsPerson(v), email: v.email } } })}
          >
            Registrar como miembro
          </Button>
        ) : null}
      </header>
      {error ? <Alert>{error}</Alert> : null}

      {v.phone ? (
        <div className="actions">
          <a className="btn btn--primary" href={`https://wa.me/${whatsappNumber(v.phone)}`} target="_blank" rel="noopener noreferrer">
            WhatsApp
          </a>
          <a className="btn btn--secondary" href={`tel:${v.phone.replace(/[^\d+]/g, '')}`}>
            Llamar
          </a>
        </div>
      ) : null}

      <Card title="Seguimiento">
        <div className="form__row">
          <div className="field">
            <label htmlFor="v-stage">Etapa</label>
            <select id="v-stage" value={v.stage} disabled={busy} onChange={(e) => void run(() => api.put(`${base}/stage`, { stage: e.target.value as VisitorStage }))}>
              {STAGES.map(([k, label]) => (
                <option key={k} value={k}>
                  {label}
                </option>
              ))}
            </select>
          </div>
          <div className="field">
            <label htmlFor="v-follower">Le da seguimiento</label>
            <SearchSelect
              id="v-follower"
              value={v.follower?.personId ?? ''}
              disabled={busy}
              onChange={(v2) => void run(() => api.put(`${base}/follower`, { personId: v2 || null }))}
              emptyLabel="Sin responsable"
              options={personOptions(v.team)}
            />
          </div>
        </div>
        <form
          className="form"
          onSubmit={(e) => {
            e.preventDefault();
            if (body.trim()) void run(() => api.post(`${base}/notes`, { kind, body: body.trim() })).then((ok) => ok && setBody(''));
          }}
        >
          <div className="segmented" role="radiogroup" aria-label="Tipo de contacto">
            {(['llamada', 'mensaje', 'visita', 'nota'] as const).map((k) => (
              <label key={k} className="check">
                <input type="radio" checked={kind === k} onChange={() => setKind(k)} /> {KIND[k]}
              </label>
            ))}
          </div>
          <div className="field">
            <label htmlFor="v-note">¿Qué pasó?</label>
            <textarea id="v-note" rows={2} maxLength={2000} value={body} onChange={(e) => setBody(e.target.value)} placeholder="Le llamé, vendrá el domingo con su esposa." />
          </div>
          <Button type="submit" busy={busy} disabled={!body.trim()}>
            Guardar
          </Button>
        </form>
        {v.notes.length > 0 ? (
          <ul className="list">
            {v.notes.map((n) => (
              <li key={n.id} className="list__item">
                <span className="agenda__body">
                  <span>
                    <strong>{KIND[n.kind] ?? n.kind}:</strong> {n.body}
                  </span>
                  <span className="muted small">
                    {n.author.name} · {new Date(n.createdAt).toLocaleString('es', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })}
                  </span>
                </span>
              </li>
            ))}
          </ul>
        ) : null}
      </Card>

      {editing ? (
        <Card title="Editar datos">
          <VisitorForm
            initial={{ person: visitorAsPerson(v), email: v.email, invitedBy: v.invitedBy, interests: v.interests, prayerRequest: v.prayerRequest }}
            submitLabel="Guardar"
            onCancel={() => setEditing(false)}
            onSubmit={async (values) => {
              await api.put(base, { ...values, firstVisit: v.firstVisit });
              setEditing(false);
              data.reload();
            }}
          />
        </Card>
      ) : (
        <Card title="Datos" action={<Button variant="ghost" onClick={() => setEditing(true)}>Editar</Button>}>
          <dl className="details">
            <dt>Teléfono</dt>
            <dd>{v.phone ?? '—'}</dd>
            <dt>Correo</dt>
            <dd>{v.email ?? '—'}</dd>
            <dt>Invitado por</dt>
            <dd>{v.invitedBy ?? '—'}</dd>
            <dt>Le interesa</dt>
            <dd>{v.interests.length ? v.interests.join(', ') : '—'}</dd>
            {v.prayerRequest ? (
              <>
                <dt>Oración</dt>
                <dd className="pre-line">{v.prayerRequest}</dd>
              </>
            ) : null}
          </dl>
        </Card>
      )}

      {v.canDelete ? (
        <p>
          <Button
            variant="ghost"
            onClick={() => {
              if (window.confirm('¿Borrar este registro de visitante?')) void run(() => api.delete(base)).then((ok) => ok && navigate('/app/miembros?tab=visitas'));
            }}
          >
            Borrar registro
          </Button>
        </p>
      ) : null}
    </div>
  );
}
